import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import API from '../../services/api';

const PacksB2BAdmin = () => {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: '', price_eur: '', points_amount: '', commission_rate: '' });
  const [saving, setSaving] = useState(false);

  const fetchPacks = async () => {
    setLoading(true);
    try {
      const res = await API.get('/admin/packs');
      setPacks(res.data.packs || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPacks(); }, []);

  const openEdit = (pack) => {
    setForm({ name: pack.name, price_eur: pack.price_eur, points_amount: pack.points_amount, commission_rate: pack.commission_rate || '' });
    setModal(pack);
  };

  const openNew = () => {
    setForm({ name: '', price_eur: '', points_amount: '', commission_rate: '' });
    setModal('new');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (modal === 'new') {
        await API.post('/admin/packs', form);
      } else {
        await API.put(`/admin/packs/${modal.id}`, { ...form, is_active: modal.is_active });
      }
      await fetchPacks();
      setModal(null);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleToggle = async (pack) => {
    try {
      await API.put(`/admin/packs/${pack.id}/toggle`);
      await fetchPacks();
    } catch (e) { console.error(e); }
  };

  const totals = {
    count: packs.length,
    active: packs.filter(p => p.is_active).length,
    sales: packs.reduce((a, p) => a + (p.sales_count || 0), 0),
    revenue: packs.reduce((a, p) => a + parseFloat(p.total_revenue || 0), 0),
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#94a3b8', flexDirection: 'column', gap: 12 }}>
        <i className="fas fa-spinner fa-spin" style={{ fontSize: 28, color: '#3b82f6' }}></i>
        <span>Chargement...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
        <button className="admin-btn-primary" onClick={openNew}>
          <i className="fas fa-plus"></i> Nouveau pack
        </button>
      </div>

      {/* Stats */}
      <div className="admin-kpi-grid">
        {[
          { icon: 'fa-cube', label: 'Total packs', value: totals.count, color: '#3b82f6' },
          { icon: 'fa-toggle-on', label: 'Actifs', value: totals.active, color: '#10b981' },
          { icon: 'fa-shopping-cart', label: 'Total achats', value: totals.sales, color: '#f59e0b' },
          { icon: 'fa-coins', label: 'Revenu B2B', value: `${totals.revenue.toFixed(2)} TND`, color: '#8b5cf6' },
        ].map((k, i) => (
          <div key={i} className="admin-kpi-card">
            <div className="admin-kpi-header">
              <div className="admin-kpi-icon" style={{ background: `${k.color}18`, color: k.color }}>
                <i className={`fas ${k.icon}`}></i>
              </div>
            </div>
            <div className="admin-kpi-value">{k.value}</div>
            <div className="admin-kpi-label">{k.label}</div>
          </div>
        ))}
      </div>

      {/* Packs grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20, marginBottom: 24 }}>
        {packs.map(pack => (
          <motion.div
            key={pack.id}
            className="admin-table-card"
            style={{ padding: 24, opacity: pack.is_active ? 1 : 0.6 }}
            whileHover={{ y: -3, boxShadow: '0 8px 24px rgba(15,23,42,0.1)' }}
            transition={{ duration: 0.2 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{pack.name}</div>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{pack.sales_count || 0} achat(s)</div>
              </div>
              {/* Toggle */}
              <div onClick={() => handleToggle(pack)} style={{ width: 44, height: 24, borderRadius: 30, cursor: 'pointer', background: pack.is_active ? '#3b82f6' : '#e2e8f0', position: 'relative', transition: 'background 0.2s' }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: pack.is_active ? 22 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}></div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1, background: '#f8fafc', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#3b82f6' }}>{parseFloat(pack.price_eur).toFixed(0)} TND</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>Prix</div>
              </div>
              <div style={{ flex: 1, background: '#f8fafc', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b' }}>{Math.round(pack.points_amount / 1000)}k</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>Points</div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <span style={{ fontSize: 13, color: '#64748b' }}>Commission</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#10b981' }}>{pack.commission_rate ?? '-'}%</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <span style={{ fontSize: 13, color: '#64748b' }}>Revenu total</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#8b5cf6' }}>{parseFloat(pack.total_revenue || 0).toFixed(2)} TND</span>
            </div>

            <button className="admin-btn-secondary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => openEdit(pack)}>
              <i className="fas fa-edit"></i> Modifier
            </button>
          </motion.div>
        ))}
        {packs.length === 0 && (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
            Aucun pack B2B. <button className="admin-btn-primary" onClick={openNew} style={{ marginLeft: 8 }}>Créer le premier pack</button>
          </div>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {modal !== null && (
          <motion.div className="admin-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={e => e.target === e.currentTarget && setModal(null)}>
            <motion.div className="admin-modal" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}>
              <div className="admin-modal-header">
                <h2>{modal === 'new' ? 'Nouveau pack B2B' : `Modifier — ${modal.name}`}</h2>
                <button className="admin-modal-close" onClick={() => setModal(null)}><i className="fas fa-times"></i></button>
              </div>
              {[
                { key: 'name', label: 'Nom du pack', type: 'text', placeholder: 'ex: Business' },
                { key: 'price_eur', label: 'Prix (TND)', type: 'number', placeholder: '400' },
                { key: 'points_amount', label: 'Points inclus', type: 'number', placeholder: '50000' },
                { key: 'commission_rate', label: 'Commission (%)', type: 'number', placeholder: '4', step: '0.1' },
              ].map(f => (
                <div className="admin-form-group" key={f.key}>
                  <label>{f.label}</label>
                  <input className="admin-form-input" type={f.type} placeholder={f.placeholder} step={f.step} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} />
                </div>
              ))}
              <div className="admin-modal-actions">
                <button className="btn-accept" onClick={handleSave} disabled={saving}>{saving ? 'En cours...' : (modal === 'new' ? 'Créer le pack' : 'Enregistrer')}</button>
                <button className="btn-cancel" onClick={() => setModal(null)}>Annuler</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PacksB2BAdmin;
